// components/admin/AdminFeedback.jsx
import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const AdminFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filter, setFilter] = useState('all');
  const [adminNote, setAdminNote] = useState('');

  const statusColors = {
    'new': 'bg-blue-500',
    'in-progress': 'bg-yellow-500',
    'resolved': 'bg-green-500',
    'closed': 'bg-gray-500',
  };

  const statusLabels = {
    'new': 'Yeni',
    'in-progress': 'İnceleniyor',
    'resolved': 'Çözüldü',
    'closed': 'Kapatıldı',
  };

  useEffect(() => {
    const q = query(
      collection(db, 'feedback'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const feedbackList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeedbacks(feedbackList);
      setLoading(false);
    }, (error) => {
      console.error('Feedback yükleme hatası:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredFeedbacks = feedbacks.filter(fb => {
    if (filter === 'all') return true;
    return fb.status === filter;
  });

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });
      
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Status güncelleme hatası:', error);
    }
  };

  const saveAdminNote = async (feedbackId) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        adminNote,
        adminNoteAt: Timestamp.now(),
      });
      
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, adminNote }));
      }
      alert('Not kaydedildi!');
    } catch (error) {
      console.error('Not kaydetme hatası:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'login': '🔐',
      'register': '📝',
      'reservation': '📅',
      'payment': '💳',
      'pin': '🔑',
      'ui': '🖥️',
      'suggestion': '💡',
      'other': '❓',
    };
    return icons[category] || '❓';
  };

  const newCount = feedbacks.filter(fb => fb.status === 'new').length;

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Geri Bildirimler
            {newCount > 0 && (
              <span className="ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                {newCount} yeni
              </span>
            )}
          </h2>
        </div>

        {/* Filtre */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'in-progress', 'resolved', 'closed'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter === status
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {status === 'all' ? 'Tümü' : statusLabels[status]}
              {status !== 'all' && (
                <span className="ml-1">
                  ({feedbacks.filter(fb => fb.status === status).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feedback Listesi */}
        <Card className="h-[600px] overflow-y-auto">
          {filteredFeedbacks.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p>Henüz geri bildirim yok</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFeedbacks.map((fb) => (
                <div
                  key={fb.id}
                  onClick={() => {
                    setSelectedFeedback(fb);
                    setAdminNote(fb.adminNote || '');
                  }}
                  className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                    selectedFeedback?.id === fb.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-transparent bg-gray-700/50 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getCategoryIcon(fb.category)}</span>
                      <div>
                        <p className="font-medium text-white text-sm">
                          {fb.categoryLabel}
                        </p>
                        <p className="text-xs text-gray-400">{fb.userEmail}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full text-white ${statusColors[fb.status]}`}>
                      {statusLabels[fb.status]}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm mt-2 line-clamp-2">
                    {fb.description}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      {formatDate(fb.createdAt)}
                    </p>
                    {fb.screenshots?.length > 0 && (
                      <span className="text-xs text-blue-400 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {fb.screenshots.length} görsel
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Detay Paneli */}
        <Card className="h-[600px] overflow-y-auto">
          {selectedFeedback ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-700 pb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{getCategoryIcon(selectedFeedback.category)}</span>
                  <div>
                    <h3 className="font-bold text-white">{selectedFeedback.categoryLabel}</h3>
                    <p className="text-sm text-gray-400">{selectedFeedback.userEmail}</p>
                  </div>
                </div>
              </div>

              {/* Status Değiştirme */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Durum</label>
                <div className="flex gap-2 flex-wrap">
                  {['new', 'in-progress', 'resolved', 'closed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateStatus(selectedFeedback.id, status)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        selectedFeedback.status === status
                          ? `${statusColors[status]} text-white`
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {statusLabels[status]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Açıklama</label>
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <p className="text-gray-200 whitespace-pre-wrap">{selectedFeedback.description}</p>
                </div>
              </div>

              {/* Screenshotlar */}
              {selectedFeedback.screenshots?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ekran Görüntüleri ({selectedFeedback.screenshots.length})
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedFeedback.screenshots.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={url}
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-600 hover:border-blue-500 transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Teknik Bilgiler */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Teknik Bilgiler</label>
                <div className="p-3 bg-gray-700/50 rounded-lg text-xs text-gray-400 space-y-1">
                  <p><strong>Tarih:</strong> {formatDate(selectedFeedback.createdAt)}</p>
                  <p><strong>Ekran:</strong> {selectedFeedback.screenSize}</p>
                  <p className="break-all"><strong>Tarayıcı:</strong> {selectedFeedback.userAgent}</p>
                </div>
              </div>

              {/* Admin Notu */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Notu</label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Bu feedback hakkında not ekleyin..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
                <Button
                  onClick={() => saveAdminNote(selectedFeedback.id)}
                  variant="secondary"
                  className="mt-2"
                >
                  Notu Kaydet
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <p>Detay görüntülemek için bir feedback seçin</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminFeedback;
